import { Component, Input, OnInit } from '@angular/core';
import {
  getFavoriteContentWithHighestRank,
  getOnlyOptionCategoriesProcessed,
  getTealiumLocation,
  isOptionActive,
  NetflixBuyAndSellStatus,
  Option,
  OptionCategory,
  OptionType,
  Product,
  TvProduct,
} from '@dch/products-business-logic';
import {
  ApplicationState,
  getOptionsCategoriesConfig,
  getSelectedProductAvailableOptionsError,
  getSelectedProductAvailableOptionsLoaded,
  getSelectedProductDetails,
} from '../../../../store/reducers';
import { combineLatest } from 'rxjs';
import { WebshopUtils } from '../../../../shared/webshop/webshop-utils';
import { OnDestroyHookDirective } from '../../../../shared/hooks';
import { Store } from '@ngrx/store';
import { CustomerService, LanguageService, TranslationService } from '@proximus/ng-common';
import { ActivatedRoute } from '@angular/router';
import * as MagnoliaConfigActions from '../../../../store/actions/magnolia-config.actions';
import {
  DISNEY_PLUS_MPUID_CODES,
  DISNEY_PLUS_PREMIUM,
  DISNEY_PLUS_STANDARD,
  SPORTS_MPUID_CODES,
  SPORTS_PREMIUM_MPUID_CODES,
  SPORTS_STANDARD_MPUID_CODES,
} from 'src/app/details/constants';

@Component({
  selector: 'mprd-tv-available-options-container',
  templateUrl: './tv-available-options-container.component.html',
})
export class TvAvailableOptionsContainerComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  OptionType = OptionType;

  @Input() isUnmigratedTvCustomer: boolean;

  activeTvBonus: Option;
  languageRestriction: string;
  selectedCategory: string;
  tealiumLocation: string;
  selectedProduct: Product;
  hasError: boolean;
  isRes: boolean;
  categories: Array<OptionCategory>;
  deepLinkCategory: string;
  availableOptionsLoaded: boolean;
  showBuyAndSell: boolean;
  disneyPlusPremiumCode = DISNEY_PLUS_PREMIUM;

  constructor(
    private store: Store<ApplicationState>,
    private customerService: CustomerService,
    private route: ActivatedRoute,
    private languageService: LanguageService,
    private readonly translationService: TranslationService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.store.dispatch(new MagnoliaConfigActions.GetOptionsCategoriesConfig());
    this.addSubscription(
      this.store
        .select(getSelectedProductAvailableOptionsError)
        .subscribe((value) => {
          this.hasError = value !== null;
        }),
    );
    this.addSubscription(
      this.store
        .select(getSelectedProductAvailableOptionsLoaded)
        .subscribe((value) => {
          this.availableOptionsLoaded = value;
        }),
    );
    this.addSubscription(
      combineLatest([
        this.route.queryParams,
        this.store.select(getOptionsCategoriesConfig),
        this.store.select(getSelectedProductDetails),
      ]).subscribe((res) => {
        const params = res[0];
        const config = res[1];
        const selectedProduct = res[2];
        if (params) {
          this.deepLinkCategory = params['cat'];
        }
        if (
          config &&
          selectedProduct &&
          JSON.stringify(this.selectedProduct) !==
            JSON.stringify(selectedProduct.tvProductDetails)
        ) {
          this.selectedProduct =
            selectedProduct.mobileProductDetails ||
            selectedProduct.tvProductDetails ||
            selectedProduct.internetProductDetails;
          this.tealiumLocation = getTealiumLocation(
            this.selectedProduct as any,
          );
          const optionCategoriesProcessed = getOnlyOptionCategoriesProcessed(
            this.selectedProduct,
            config,
            this.languageService.getSelectedLanguage(),
            this.deepLinkCategory,
          );
          this.categories = optionCategoriesProcessed.categories;
          this.selectCategory(optionCategoriesProcessed.selectedCategoryId);

          this.activeTvBonus =
            this.selectedProduct &&
            getFavoriteContentWithHighestRank(this.selectedProduct);
          WebshopUtils.publishCartRetrival(
            this.selectedProduct.installationId,
            this.selectedProduct.accessNumber,
            this.selectedProduct.plc,
          );

          this.showBuyAndSell =
            !!(this.selectedProduct as TvProduct).netflixBuyAndSell &&
            (this.selectedProduct as TvProduct).netflixBuyAndSell ===
              NetflixBuyAndSellStatus.NOT_ELIGIBLE;

          this.processDisneyPlusOptions();
           (this.translationService.getTranslatedValueFor(
        'tv.isSportsFeatureFlag',
      ) ?? 'false') === 'true' && this.processSportsOptions();
        }
      }),
    );
  }

  processSportsOptions() {
    const { options } = this.selectedProduct;

    // Filter out and get all Disney+ options
    const sportsOptions = options.filter((option) =>
      SPORTS_MPUID_CODES.includes(option.optionCodeForDescription),
    );

    if (sportsOptions.length < 2) return;

    // Remove all Disney+ options from product
    this.selectedProduct.options = options.filter(
      (option) => !SPORTS_MPUID_CODES.includes(option.optionCodeForDescription),
    );

    // Add back either Premium (if active) or Standard option
    const optionToAdd =
      sportsOptions.find(
        (option) =>
          SPORTS_PREMIUM_MPUID_CODES.includes(
            option.optionCodeForDescription,
          ) && isOptionActive(option),
      ) ||
      sportsOptions.find((option) =>
        SPORTS_STANDARD_MPUID_CODES.includes(option.optionCodeForDescription),
      );

    if (optionToAdd) {
      this.selectedProduct.options.push(optionToAdd);
    }
  }

  processDisneyPlusOptions() {
    const { options } = this.selectedProduct;

    // Filter out and get all Disney+ options
    const disneyPlusOptions = options.filter((option) =>
      DISNEY_PLUS_MPUID_CODES.includes(option.optionCodeForDescription),
    );

    if (disneyPlusOptions.length < 2) return;

    // Remove all Disney+ options from product
    this.selectedProduct.options = options.filter(
      (option) =>
        !DISNEY_PLUS_MPUID_CODES.includes(option.optionCodeForDescription),
    );

    // Add back either Premium (if active) or Standard option
    const optionToAdd =
      disneyPlusOptions.find(
        (option) =>
          option.optionCodeForDescription === DISNEY_PLUS_PREMIUM &&
          isOptionActive(option),
      ) ||
      disneyPlusOptions.find(
        (option) => option.optionCodeForDescription === DISNEY_PLUS_STANDARD,
      );

    if (optionToAdd) {
      this.selectedProduct.options.push(optionToAdd);
    }
  }

  selectCategory(cat: string) {
    this.selectedCategory = cat;
  }

  setLanguageRestriction(language) {
    this.languageRestriction = language;
  }
}
